import { validateInvoiceStatus } from "@/types/invoiceStatus";
import { useEffect, useReducer } from "react";
import { StyledTableBisManualPag } from "@/components/styled/StyledTableBisManualPag";

import { InvoiceValidationModal } from "../invoiceValidationModal";
import { InvoiceDeletionModal } from "../invoiceDeletionModal";
import usePagination from "@/hooks/useSyncedPagination";
import { useGetInvoiceBoV1 } from "@/api/invoice";
import { useInvoiceTableColumns } from "./invoiceTableColumns";
import { InvoicesFilter } from "./invoicesFilter";
import { InvoiceStats } from "./invoiceStats";
import { InvoiceEditModal } from "../invoiceEditModal";
import { InvoicesDownloadPdf } from "./invoicesDownloadPdf";
import { useGetRibs } from "@/api/rib.api";
import {
  invoiceTableReducer,
  initialState,
  createFilterAction,
  createModalAction,
} from "./invoiceTableReducer";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { useDisplayDocument } from "@/hooks/useDisplayDocument";
import { InvoiceStatusEnum } from "@/constants/enums/invoiceStatus.enum";

type InvoiceTableProps = {
  companyId?: string;
  billingEntityId?: string | null;
  initMonth?: boolean;
};

export const InvoiceTable = ({
  companyId,
  billingEntityId,
  initMonth = true,
}: InvoiceTableProps) => {
  const [state, dispatch] = useReducer(invoiceTableReducer, {
    ...initialState,
  });
  const user = useAtomValue(userAtom);
  const [pagination, setPagination] = usePagination("invoiceBo");

  const { displayDocument } = useDisplayDocument();

  const { data: ribs } = useGetRibs({
    isDeleted: true,
  });

  const { data: invoices } = useGetInvoiceBoV1({
    paymentStatus: state.filters.statusField
      ? validateInvoiceStatus(state.filters.statusField)
      : undefined,
    invoiceId: state.filters.searchField || undefined,
    businessName: state.filters.searchField || undefined,
    companyId: companyId || undefined,
    billingEntityId: billingEntityId || undefined,
    ribId: state.filters.ribId || undefined,
    year: state.filters.year,
    month: state.filters.month?.map((m) => m),
    status: [InvoiceStatusEnum.VALIDATED],
    amountToBillTop: state.filters.amountToBill[1]
      ? state.filters.amountToBill[1] * 100
      : undefined,
    amountToBillBottom: state.filters.amountToBill[0]
      ? state.filters.amountToBill[0] * 100
      : undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [setPagination, state, companyId, billingEntityId]);

  const columns = useInvoiceTableColumns(
    displayDocument,
    user?.role || null,
    (invoice) => dispatch(createModalAction("selectedInvoice", invoice)),
    (id) => dispatch(createModalAction("invoiceDeleteModal", id)),
    (invoice) => dispatch(createModalAction("invoiceToEdit", invoice)),
  );

  return (
    <>
      <div className="flex w-full flex-col items-center justify-between gap-2 xl:flex-row">
        <InvoicesFilter
          setSearchField={(value) => dispatch(createFilterAction("searchField", value))}
          searchField={state.filters.searchField}
          setStatusField={(value) => dispatch(createFilterAction("statusField", value))}
          statusField={state.filters.statusField}
          setYear={(value) => dispatch(createFilterAction("year", value))}
          setMonth={(value) => dispatch(createFilterAction("month", value))}
          initMonth={initMonth}
          setRibId={(value) => dispatch(createFilterAction("ribId", value))}
          ribId={state.filters.ribId}
          ribs={ribs || []}
          setDisplayStats={(value) => dispatch(createModalAction("displayStats", value))}
          setDisplayDownloadPdf={(value) =>
            dispatch(createModalAction("displayDownloadPdf", value))
          }
          hasCompanyId={!!companyId}
          displayStats={state.modals.displayStats}
          displayDownloadPdf={state.modals.displayDownloadPdf}
          setAmountToBill={(value) => dispatch(createFilterAction("amountToBill", value))}
          amountToBill={state.filters.amountToBill}
        />
      </div>
      {state.modals.displayStats ? (
        <InvoiceStats
          year={state.filters.year}
          month={state.filters.month}
          paymentStatus={state.filters.statusField}
          businessName={state.filters.searchField}
          invoiceId={state.filters.searchField}
        />
      ) : (
        <StyledTableBisManualPag
          data={invoices?.data || []}
          columns={columns}
          setPagination={setPagination}
          pagination={pagination}
          rowCount={invoices?.count || 0}
        />
      )}
      <InvoiceValidationModal
        closeModal={() => dispatch(createModalAction("selectedInvoice", null))}
        invoice={state.modals.selectedInvoice}
      />
      <InvoiceDeletionModal
        closeModal={() => dispatch(createModalAction("invoiceDeleteModal", null))}
        id={state.modals.invoiceDeleteModal}
      />
      {state.modals.invoiceToEdit && (
        <InvoiceEditModal
          closeModal={() => dispatch(createModalAction("invoiceToEdit", null))}
          invoice={state.modals.invoiceToEdit}
        />
      )}
      {state.modals.displayDownloadPdf && (
        <InvoicesDownloadPdf
          closeModal={() => dispatch(createModalAction("displayDownloadPdf", false))}
        />
      )}
    </>
  );
};
